import { supabase } from './supabase'

const STORAGE_KEY = 'geo_session'
const VERIFY_GEO_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-geo`

export interface GeoSession {
  galleryId: string
  /** Opaque UUID token validated server-side via geo_sessions table */
  token: string
  /** Expiry in milliseconds (matches the DB row expires_at) */
  expiresAt: number
}

export function getGeoSession(galleryId: string): GeoSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const session: GeoSession = JSON.parse(raw)
    // Reject sessions from the old JWT-based format (had 'jwt' instead of 'token')
    if (!session.token) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    if (session.galleryId !== galleryId) return null
    if (Date.now() >= session.expiresAt) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    return session
  } catch {
    return null
  }
}

/**
 * Reads whatever geo session is currently stored, without requiring a galleryId.
 * Unlike `getGeoSession`, this does not verify the gallery matches — used to
 * identify an anonymous Sentry visitor by their active geo session token.
 */
export function getCurrentGeoSession(): GeoSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const session: GeoSession = JSON.parse(raw)
    if (!session.token) return null
    if (Date.now() >= session.expiresAt) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    return session
  } catch {
    return null
  }
}

export function setGeoSession(galleryId: string, token: string, expiresAt: string): GeoSession {
  const session: GeoSession = {
    galleryId,
    token,
    expiresAt: new Date(expiresAt).getTime(),
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  return session
}

export function clearGeoSession(): void {
  localStorage.removeItem(STORAGE_KEY)
}

/**
 * Deletes the geo session token from the DB, preventing reuse even within its
 * remaining TTL. Fire-and-forget — caller should not block on this.
 */
export async function revokeGeoSession(token: string): Promise<void> {
  await supabase.rpc('revoke_geo_session', { p_token: token })
}

export type GeoVerifyResult =
  | { ok: true; token: string; expiresAt: string }
  | { ok: false; reason: 'outside_range' | 'location_denied' | 'gallery_not_found' | 'error' }

/**
 * Requests the device GPS position and calls the verify-geo Edge Function.
 * On success returns an opaque session token (UUID) stored in the geo_sessions table.
 */
export async function verifyGeo(galleryId: string): Promise<GeoVerifyResult> {
  let coords: GeolocationCoordinates
  try {
    coords = await new Promise<GeolocationCoordinates>((resolve, reject) => {
      // Hard cap so a pending/broken permission prompt can't hang the lock screen.
      // getCurrentPosition's own `timeout` only starts counting after the user
      // answers the permission prompt, so a never-answered prompt hangs forever.
      const timeoutId = setTimeout(() => reject(new Error('location_timeout')), 12000)
      navigator.geolocation.getCurrentPosition(
        (pos) => { clearTimeout(timeoutId); resolve(pos.coords) },
        (err) => { clearTimeout(timeoutId); reject(err) },
        { enableHighAccuracy: true, timeout: 10000 },
      )
    })
  } catch {
    return { ok: false, reason: 'location_denied' }
  }

  try {
    const res = await fetch(VERIFY_GEO_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY as string}`,
      },
      body: JSON.stringify({
        galleryId,
        userLat: coords.latitude,
        userLng: coords.longitude,
      }),
    })

    if (res.ok) {
      const { token, expiresAt } = await res.json()
      return { ok: true, token, expiresAt }
    }

    const body = await res.json().catch(() => ({ error: 'error' }))
    if (res.status === 404) return { ok: false, reason: 'gallery_not_found' }
    if (body.error === 'outside_range') return { ok: false, reason: 'outside_range' }
    return { ok: false, reason: 'error' }
  } catch {
    return { ok: false, reason: 'error' }
  }
}
